// src/components/ContactForm.tsx
import { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { useNetlifyForm } from '@/hooks/useNetlifyForm';

interface ContactFormProps {
  className?: string;
}

const ContactForm = ({ className }: ContactFormProps) => {
  // Use our custom Netlify form hook
  const { handleSubmit, formStatus, resetForm } = useNetlifyForm('contact');

  // Reset form status after a delay when submission is successful
  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (formStatus === 'success') {
      timer = setTimeout(() => {
        resetForm();
      }, 3000);
    }
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [formStatus, resetForm]);

  return (
    <div className={className}>
      <form onSubmit={handleSubmit}>
        {/* Hidden input for Netlify form name */}
        <input type="hidden" name="form-name" value="contact" />

        {/* Bot field honeypot - helps avoid spam */}
        <p className="hidden">
          <label>
            Don't fill this out if you're human: <input name="bot-field" />
          </label>
        </p>

        <div>
          <input
            type="text"
            id="name"
            name="name"
            required
            className="w-full px-5 py-4 mb-8 bg-gray-50 border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-secondary focus:border-transparent"
            placeholder="Your name"
            disabled={formStatus === 'submitting'}
          />
        </div>

        <div>
          <input
            type="email"
            id="email"
            name="email"
            required
            className="w-full px-5 py-4 mb-8 bg-gray-50 border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-secondary focus:border-transparent"
            placeholder="your@email.com"
            disabled={formStatus === 'submitting'}
          />
        </div>

        <div>
          <textarea
            id="message"
            name="message"
            rows={5}
            required
            className="w-full px-5 py-4 bg-gray-50 border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-secondary focus:border-transparent"
            placeholder="Tell us about your project"
            disabled={formStatus === 'submitting'}></textarea>
        </div>

        <div className="mt-8">
          <Button
            type="submit"
            disabled={formStatus === 'submitting'}
            className="font-semibold py-6 px-10"
            size="lg">
            {formStatus === 'submitting' ? 'Sending...' : 'Send Message'}
          </Button>
        </div>

        {formStatus === 'success' && (
          <motion.div
            className="p-6 bg-green-50 text-green-700 rounded-md mt-8 flex items-center"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}>
            <div className="bg-green-100 rounded-full p-2 mr-4">
              <svg
                className="w-6 h-6 text-green-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M5 13l4 4L19 7"></path>
              </svg>
            </div>
            <p className="font-medium">
              Your message has been sent successfully. We'll get back to you
              soon!
            </p>
          </motion.div>
        )}

        {formStatus === 'error' && (
          <motion.div
            className="p-6 bg-red-50 text-red-700 rounded-md mt-8 flex items-center"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}>
            <div className="bg-red-100 rounded-full p-2 mr-4">
              <svg
                className="w-6 h-6 text-red-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </div>
            <p className="font-medium">
              There was an error sending your message. Please try again.
            </p>
          </motion.div>
        )}
      </form>
    </div>
  );
};

export default ContactForm;
